import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  CheckCircle2, 
  AlertCircle, 
  Users, 
  TrendingUp, 
  ShieldCheck,
  ArrowRight,
  Loader2,
  Lock,
  ExternalLink,
  Crown,
  Infinity,
  Zap,
  ChevronRight,
  ShieldAlert,
  HelpCircle,
  Clock,
  Sparkles,
  MessageSquare,
  Shield,
  Star,
  Building2,
  Check,
  Plus
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import ContactModal from '../components/ContactModal';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { io } from 'socket.io-client';
import Skeleton from '../components/Skeleton';

// --- HELPER COMPONENTS ---

const MemberAvatar = ({ email, size = "w-10 h-10" }) => {
  const initials = email?.substring(0, 2).toUpperCase() || '?';

  return (
    <div className={`${size} rounded-full border-2 border-[#03060b] overflow-hidden shadow-xl flex items-center justify-center bg-gradient-to-br from-primary/40 to-secondary/40 relative group`}>
      <span className="text-[10px] font-black text-white tracking-widest">{initials}</span>
      <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};

const FeatureComparison = ({ t }) => {
  const plans = [
    {
      id: 'free',
      name: t('plan_free'),
      price: '$0',
      label: t('val_free_forever'),
      icon: Shield,
      color: 'slate',
      features: [
        { name: 'unique_users', value: t('limit_5_users') },
        { name: 'total_projects', value: t('limit_3_projects') },
        { name: 'screenshots_limit', value: t('limit_15_screenshots') },
        { name: 'storage_limit', value: t('limit_100mb') }
      ]
    },
    {
      id: 'pro',
      name: t('plan_pro'),
      price: '$10k',
      label: t('val_per_user'),
      icon: Crown,
      color: 'amber',
      featured: true,
      features: [
        { name: 'unique_users', value: t('limit_20_users'), highlight: true },
        { name: 'total_projects', value: t('limit_10_projects'), highlight: true },
        { name: 'screenshots_limit', value: t('limit_100_screenshots'), highlight: true },
        { name: 'storage_limit', value: t('limit_2gb'), highlight: true }
      ]
    },
    {
      id: 'enterprise',
      name: t('plan_enterprise'),
      price: '$25k',
      label: t('val_per_user'),
      icon: Building2,
      color: 'primary',
      features: [
        { name: 'unique_users', value: t('limit_50_users'), highlight: true },
        { name: 'total_projects', value: t('limit_50_projects'), highlight: true },
        { name: 'screenshots_limit', value: t('limit_500_screenshots'), highlight: true },
        { name: 'storage_limit', value: t('limit_10gb'), highlight: true }
      ]
    }
  ];

  return (
    <div className="w-full space-y-16">
      <div className="text-center">
        <h3 className="text-5xl font-display font-black text-white tracking-tight uppercase leading-none">{t('plans_comparison')}</h3>
        <p className="text-xs text-slate-500 font-black tracking-[0.4em] uppercase mt-6">{t('level_comparison')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
        {plans.map((plan) => (
          <motion.div 
            key={plan.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`relative rounded-2xl p-10 border transition-all duration-700 cursor-default group flex flex-col h-full backdrop-blur-xl ${
              plan.featured 
              ? 'bg-gradient-to-b from-amber-500/10 via-white/[0.02] to-transparent border-amber-500/40 shadow-[0_15px_30px_rgba(245,158,11,0.1)]' 
              : plan.id === 'enterprise'
                ? 'bg-gradient-to-b from-primary/10 via-white/[0.02] to-transparent border-primary/30 hover:border-primary/50 shadow-[0_30px_60px_rgba(37,99,235,0.1)]'
                : 'bg-white/[0.03] border-white/10 hover:border-white/20'
            }`}
          >
            {plan.featured && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-10 py-3 rounded-2xl bg-amber-500 text-slate-900 text-[11px] font-black uppercase tracking-[0.25em] shadow-[0_10px_20px_rgba(245,158,11,0.3)] flex items-center gap-3 z-20">
                <Star size={14} fill="currentColor" className="animate-pulse" />
                {t('recommended')}
              </div>
            )}

            <div className="flex items-center gap-6 mb-10">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all group-hover:scale-110 group-hover:rotate-3 duration-500 shadow-2xl ${
                plan.featured ? 'bg-amber-500 text-slate-900' : plan.id === 'enterprise' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400'
              }`}>
                <plan.icon size={30} strokeWidth={2.5} />
              </div>
              <div>
                <h4 className="text-2xl font-black text-white leading-none">{plan.name}</h4>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className={`text-3xl font-black ${plan.featured ? 'text-amber-500' : plan.id === 'enterprise' ? 'text-primary' : 'text-white'}`}>{plan.price}</span>
                  <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-500">{plan.label}</p>
                </div>
              </div>
            </div>

            <div className="space-y-8 flex-grow">
              {plan.features.map((f, i) => (
                <div key={i} className="flex flex-col gap-2.5 group/feat">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest group-hover/feat:text-slate-300 transition-colors">{t(f.name)}</span>
                    <span className={`text-[11px] font-black ${f.highlight ? (plan.id === 'enterprise' ? 'text-primary' : 'text-amber-400') : 'text-slate-400'}`}>
                      {f.value}
                    </span>
                  </div>
                  <div className={`h-1.5 w-full rounded-2xl overflow-hidden p-[1px] ${plan.featured ? 'bg-amber-500/10' : plan.id === 'enterprise' ? 'bg-primary/10' : 'bg-white/5'}`}>
                    <motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: plan.id === 'free' ? '20%' : plan.id === 'pro' ? '70%' : '100%' }}
                      transition={{ delay: 0.3 + i * 0.1, duration: 1.5, ease: "circOut" }}
                      className={`h-full rounded-full ${
                        plan.featured 
                        ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600' 
                        : plan.id === 'enterprise'
                          ? 'bg-gradient-to-r from-primary via-blue-500 to-blue-600'
                          : 'bg-slate-700'
                      }`} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const FaqModal = ({ isOpen, onClose, onContactClick, t }) => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { q: t('faq_q1'), a: t('faq_a1') },
    { q: t('faq_q2'), a: t('faq_a2') },
    { q: t('faq_q3'), a: t('faq_a3') },
    { q: t('faq_q4'), a: t('faq_a4') }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] flex items-center justify-center p-6 sm:p-10"
      >
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />
        
        <motion.div 
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-2xl bg-slate-900/40 backdrop-blur-3xl border border-white/10 rounded-2xl overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.8)]"
        >
          <div className="p-10 sm:p-12 space-y-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-black text-white uppercase tracking-tight italic">{t('faq_title')}</h2>
                <p className="text-[10px] font-black text-primary uppercase tracking-[0.4em] mt-2">{t('support_billing')}</p>
              </div>
              <button 
                onClick={onClose}
                className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all"
              >
                <Plus className="rotate-45" size={24} />
              </button>
            </div>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-4 scrollbar-hide">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-white/5 rounded-2xl overflow-hidden bg-white/[0.02]">
                  <button 
                    onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
                    className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors group"
                  >
                    <span className="text-sm font-bold text-white pr-8">{faq.q}</span>
                    <ChevronRight 
                      size={18} 
                      className={`text-slate-500 transition-transform duration-300 ${openIndex === i ? 'rotate-90 text-primary' : ''}`} 
                    />
                  </button>
                  <AnimatePresence>
                    {openIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <div className="px-6 pb-6 text-sm text-slate-400 leading-relaxed font-medium">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl bg-primary/20 flex items-center justify-center text-primary">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <p className="text-[10px] font-black text-white uppercase tracking-widest">{t('still_have_questions')}</p>
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest" dangerouslySetInnerHTML={{ __html: t('response_time_2h') }}></p>
                </div>
              </div>
              <button 
                onClick={onContactClick}
                className="px-8 py-4 rounded-2xl bg-primary text-white text-[10px] font-black uppercase tracking-[0.3em] hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 flex items-center justify-center text-center"
              >
                {t('contact_support')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const WorkspaceNavSkeleton = () => (
   <div className="fixed top-0 left-0 right-0 z-[60] w-full backdrop-blur-xl bg-black/20 border-b border-white/5">
     <div className="max-w-7xl mx-auto px-10 h-20 flex items-center justify-between gap-8">
       <div className="flex-1 flex items-center gap-3 py-2 overflow-hidden">
         {[1, 2, 3].map(i => (
           <div key={i} className="px-6 py-3 rounded-2xl bg-white/5 border border-white/5 flex items-center gap-2.5">
             <Skeleton width="14px" height="14px" borderRadius="4px" />
             <Skeleton width="80px" height="10px" />
           </div>
         ))}
       </div>
       <div className="hidden lg:flex items-center gap-2">
         <Skeleton width="12px" height="12px" borderRadius="50%" />
         <Skeleton width="60px" height="8px" />
       </div>
     </div>
   </div>
);

const PricingCardSkeleton = () => (
   <div className="w-full max-w-md relative p-10 rounded-2xl bg-black/60 backdrop-blur-3xl border border-white/10 flex flex-col gap-10 text-center">
      <div className="w-24 h-24 rounded-2xl bg-white/5 flex items-center justify-center self-center shadow-2xl">
         <Skeleton width="48px" height="48px" borderRadius="1rem" />
      </div>
      <div className="space-y-4">
         <div className="flex justify-center"><Skeleton width="120px" height="10px" /></div>
         <div className="flex justify-center items-center gap-2">
            <div className="mt-4"><Skeleton width="20px" height="24px" /></div>
            <Skeleton width="180px" height="60px" borderRadius="0.75rem" />
         </div>
      </div>
      <div className="space-y-8">
         <Skeleton width="100%" height="70px" borderRadius="1.25rem" />
         <div className="flex justify-center"><Skeleton width="200px" height="10px" /></div>
      </div>
   </div>
);

const SubscriptionSkeleton = () => (
   <div className="min-h-screen bg-[#03060b] relative overflow-hidden flex items-center">
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-[10%] -left-[5%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[150px] opacity-40" />
        <div className="absolute top-[40%] -right-[10%] w-[50%] h-[50%] bg-amber-500/10 rounded-full blur-[180px] opacity-30" />
        <div className="absolute -bottom-[10%] left-[20%] w-[35%] h-[35%] bg-blue-500/15 rounded-full blur-[160px] opacity-40" />
      </div>
      
      <WorkspaceNavSkeleton />
      
      <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center px-10 pt-20 relative z-10">
         <div className="lg:col-span-7 space-y-10">
            <div className="flex gap-5">
               <Skeleton width="160px" height="56px" borderRadius="1rem" />
               <Skeleton width="200px" height="56px" borderRadius="1rem" />
            </div>
            <div className="space-y-4">
               <Skeleton width="100%" height="70px" />
               <Skeleton width="80%" height="70px" />
               <Skeleton width="60%" height="70px" />
            </div>
            <Skeleton width="400px" height="20px" />
            <div className="flex items-center gap-10 pt-4">
               <div className="flex -space-x-4">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} width="56px" height="56px" borderRadius="50%" />)}
               </div>
               <Skeleton width="240px" height="54px" borderRadius="1rem" />
            </div>
         </div>
         <div className="lg:col-span-5 flex justify-end">
            <PricingCardSkeleton />
         </div>
      </div>
   </div>
);


const Subscription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const { activeWorkspaceId, workspaces, refreshProjects, projects, loading: projectLoading } = useProject();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [selectedTier, setSelectedTier] = useState('pro');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [contactSource, setContactSource] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'pending', 'error'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const workspaceId = params.get('workspaceId') || selectedWorkspaceId;

    if (status === 'success' && workspaceId) {
      setPaymentStatus('success');
      setVerifying(true);
      
      // Mandatory Verification Handshake for Instant Upgrade
      const verifySubscription = async () => {
        try {
          // Handshake: Manual verification for workspace
          await api.post('/billing/verify-subscription', { workspaceId });
          // Force refresh the global context data (sidebar, usage, tier)
          await refreshProjects();
        } catch (err) {
          // Handshake: Verification failed
        } finally {
          setVerifying(false);
          // Clear URL params without reload
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };

      verifySubscription();
    } else if (status) {
      setPaymentStatus(status);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [selectedWorkspaceId, refreshProjects]);

  // Initialize selected workspace from context
  useEffect(() => {
    if (!selectedWorkspaceId && workspaces.length > 0) {
      const owned = workspaces.filter(ws => ws.ownerEmail?.toLowerCase() === user?.email?.toLowerCase());
      if (owned.length > 0) {
        setSelectedWorkspaceId(owned[0]._id);
      } else {
        setSelectedWorkspaceId(workspaces[0]._id);
      }
    }
  }, [workspaces, selectedWorkspaceId, user]);

  const socketRef = useRef(null);

  // Real-time Success Listener
  useEffect(() => {
    if (!activeWorkspaceId || socketRef.current) return;

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4000';
    const socket = io(socketUrl, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling'], 
      reconnectionAttempts: 5,
      timeout: 10000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      // SOCKET: Connected to workspace room
      socket.emit('join-workspace', activeWorkspaceId);
    });

    socket.on('workspace-upgraded', (data) => {
       if (data.workspaceId === activeWorkspaceId) {
         setPaymentSuccess(data.tier);
         refreshProjects();
       }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      const ws = workspaces.find(w => w._id === selectedWorkspaceId);
      setActiveWorkspace(ws);
      if (ws?.subscription?.tier === 'enterprise') {
        setSelectedTier('enterprise');
      } else {
        setSelectedTier('pro');
      }
    }
  }, [activeWorkspaceId, workspaces]);

  const sub = activeWorkspace?.subscription || { status: 'trial', tier: 'free' };
  
  const workspaceProjects = projects.filter(p => p.workspaceId === selectedWorkspaceId);
  const uniqueUsers = new Set();
  
  workspaceProjects.forEach(project => {
    if (project.ownerEmail) uniqueUsers.add(project.ownerEmail.toLowerCase());
    project.members?.forEach(m => {
      if (m.role !== 'client') uniqueUsers.add(m.email.toLowerCase());
    });
  });

  const memberCount = Math.max(uniqueUsers.size, 1);
  const costPerMember = selectedTier === 'enterprise' ? 25000 : 10000;
  const monthlyCost = memberCount * costPerMember;

  const handleSubscribe = async () => {
    if (!selectedWorkspaceId) return;
    setRedirecting(true);
    setError(null);
    try {
      const res = await api.post('/billing/create-subscription', { 
        workspaceId: selectedWorkspaceId,
        tier: selectedTier
      });

      if (res.data.init_point) {
        window.location.href = res.data.init_point;
      } else {
        throw new Error('Failed to create subscription preference');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading || projectLoading || verifying || redirecting) return <SubscriptionSkeleton />;


  return (
    <div className="fixed inset-0 w-full h-full overflow-y-auto snap-y snap-mandatory scroll-smooth bg-[#03060b] select-none custom-snap-scroll hide-scrollbar z-50">
      
      {/* Unified Atmospheric Background Layer */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {/* Master Background Glows */}
        <motion.div 
          animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-[10%] -left-[5%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[150px] opacity-40" 
        />
        <motion.div 
          animate={{ x: [0, -40, 0], y: [0, 60, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[40%] -right-[10%] w-[50%] h-[50%] bg-amber-500/10 rounded-full blur-[180px] opacity-30" 
        />
        <motion.div 
          animate={{ x: [-50, 50, -50], y: [50, -50, 50], scale: [1.2, 1, 1.2] }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-[10%] left-[20%] w-[35%] h-[35%] bg-blue-500/15 rounded-full blur-[160px] opacity-40" 
        />
        
        {/* Subtle Noise for Depth */}
      </div>

      {!loading && workspaces.length === 0 ? (
        <div className="relative z-10 min-h-screen flex items-center justify-center p-10">
          <div className="max-w-md w-full rounded-2xl glass-card p-12 text-center border border-white/10 bg-white/5 space-y-8">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto">
              <Building2 size={36} />
            </div>
            <div className="space-y-4">
              <h2 className="text-3xl font-black text-white uppercase tracking-tighter italic">{t('no_workspaces_found')}</h2>
              <p className="text-slate-400 font-medium leading-relaxed">
                {t('only_pm_manage_sub')}
              </p>
            </div>
            <button 
              onClick={() => navigate('/projects')}
              className="w-full py-5 bg-white/5 hover:bg-white/10 text-white rounded-2xl font-black uppercase tracking-[0.4em] text-[10px] transition-all border border-white/10"
            >
              {t('goProjects')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Workspace Selection - Floating Header */}
      <div className="fixed top-0 left-0 right-0 z-[60] w-full backdrop-blur-xl bg-black/20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-10 h-20 flex items-center justify-between gap-8">
          <div className="flex-1 flex items-center gap-3 overflow-x-auto scrollbar-hide py-2">
            {workspaces.map(ws => (
              <button
                key={ws._id}
                onClick={() => setSelectedWorkspaceId(ws._id)}
                className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl border transition-all whitespace-nowrap active:scale-95 text-[10px] font-black uppercase tracking-widest relative group overflow-hidden ${selectedWorkspaceId === ws._id
                    ? 'text-white border-primary/20 shadow-[0_10px_30px_rgba(37,99,235,0.15)]'
                    : 'bg-white/5 text-slate-500 border-white/5 hover:border-white/10 hover:text-slate-300'
                  }`}
              >
                {selectedWorkspaceId === ws._id && (
                  <motion.div
                    layoutId="active-ws-nav"
                    className="absolute inset-0 bg-primary shadow-[inset_0_0_15px_rgba(255,255,255,0.1)]"
                    transition={{ duration: 0.4, ease: "circOut" }}
                  />
                )}
                <Building2 size={14} className="relative z-10" />
                <span className="relative z-10">{ws.name}</span>
              </button>
            ))}
          </div>
          <button 
            onClick={() => setShowFaq(true)}
            className="hidden lg:flex items-center gap-2 text-[9px] font-black uppercase text-slate-500 tracking-[0.3em] hover:text-white transition-colors"
          >
            <HelpCircle size={12} />
            {t('faq_title')}
          </button>
        </div>
      </div>

      <ContactModal 
        isOpen={!!contactSource} 
        onClose={() => setContactSource(null)} 
        t={t} 
        initialEmail={user?.email || ''} 
        source={contactSource}
      />

      <AnimatePresence>
        {showFaq && (
          <FaqModal 
            isOpen={showFaq} 
            onClose={() => setShowFaq(false)} 
            onContactClick={() => {
              setShowFaq(false);
              setContactSource('support');
            }}
            t={t} 
          />
        )}
      </AnimatePresence>

      {/* Payment Status Overlay */}
      <AnimatePresence>
        {paymentStatus && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-[70] w-full max-w-md px-6"
          >
            <div className={`p-6 rounded-2xl border backdrop-blur-3xl shadow-2xl flex items-center gap-5 ${
              paymentStatus === 'success' 
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
              : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
            }`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                paymentStatus === 'success' ? 'bg-emerald-500 text-slate-950' : 'bg-amber-500 text-slate-950'
              }`}>
                {paymentStatus === 'success' ? <CheckCircle2 size={24} /> : <Clock size={24} />}
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest">
                  {paymentStatus === 'success' ? t('payment_confirmed') : t('payment_pending')}
                </p>
                <p className="text-xs font-medium opacity-80">
                  {paymentStatus === 'success' 
                    ? t('workspace_updated_success') 
                    : t('processing_subscription_wait')}
                </p>
              </div>
              <button onClick={() => setPaymentStatus(null)} className="ml-auto opacity-40 hover:opacity-100 transition-opacity">
                <Plus className="rotate-45" size={20} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SECTION 1: HERO & PRICING SUMMARY */}
      <section className="w-full h-screen snap-center flex flex-col justify-center items-center relative px-10 pt-20 overflow-hidden">
        <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-8 text-center lg:text-left">
             <div className="flex flex-wrap justify-center lg:justify-start gap-5">
                {[
                  { id: 'pro', icon: Crown, label: t('plan_pro'), color: 'amber' },
                  { id: 'enterprise', icon: Building2, label: t('plan_enterprise'), color: 'primary' }
                ].map(tier => (
                  <button 
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={`px-10 py-4 rounded-2xl border text-[11px] font-black uppercase tracking-[0.35em] transition-all flex items-center gap-4 ${
                      selectedTier === tier.id 
                      ? tier.color === 'amber' 
                        ? 'bg-amber-500 text-slate-950 border-amber-500 shadow-[0_10px_25px_rgba(245,158,11,0.25)]' 
                        : 'bg-primary text-white border-primary shadow-[0_15px_40px_rgba(37,99,235,0.35)]' 
                      : 'bg-white/5 text-slate-500 border-white/10 hover:border-white/20 hover:text-slate-300'
                    }`}
                  >
                    <tier.icon size={18} fill={selectedTier === tier.id ? "currentColor" : "none"} />
                    {tier.label}
                  </button>
                ))}
             </div>

             <h1 className="text-5xl md:text-7xl font-display font-black text-white leading-[0.9] tracking-tighter">
                {t('elevate_workspace')} <br />
                <span className={`text-transparent bg-clip-text bg-gradient-to-r pb-2 inline-block ${selectedTier === 'enterprise' ? 'from-primary via-blue-200 to-white' : 'from-amber-400 via-white to-amber-200'} animate-gradient-x`}>
                  {activeWorkspace?.name}
                </span>
             </h1>
             <p className="text-slate-400 text-xl leading-relaxed font-medium mx-auto lg:mx-0 max-w-xl opacity-80 italic">
                {t('workspace_billing_desc')}
             </p>
             
             <div className="flex flex-wrap items-center justify-center lg:justify-start gap-10 pt-4">
               <div className="flex items-center -space-x-4">
                 {Array.from(uniqueUsers).slice(0, 6).map((u, i) => (
                   <div key={i} className="hover:z-50 hover:-translate-y-2 transition-all duration-300">
                     <MemberAvatar email={u} size="w-14 h-14" />
                   </div>
                 ))}
                 {uniqueUsers.size > 6 && (
                   <div className="w-14 h-14 rounded-2xl bg-slate-800 border-2 border-[#03060b] flex items-center justify-center text-[11px] font-black text-white shadow-2xl z-20">
                      +{uniqueUsers.size - 6}
                   </div>
                 )}
               </div>
               <div className="text-[11px] font-black text-slate-400 uppercase tracking-[0.25em] bg-white/[0.03] px-8 py-5 rounded-2xl border border-white/5 flex items-center gap-4 hover:border-white/10 transition-colors cursor-default backdrop-blur-xl">
                 <Users size={16} className={selectedTier === 'enterprise' ? 'text-primary' : 'text-amber-500'} />
                 <span className="text-white">{memberCount} {t('unique_billable_users')}</span>
               </div>
             </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div 
               key={selectedTier}
               initial={{ opacity: 0, scale: 0.8, y: 50 }}
               animate={{ opacity: 1, scale: 1, y: 0 }}
               transition={{ type: "spring", bounce: 0.3, duration: 1 }}
               className="w-full max-w-md relative group z-10"
            >
               <div className={`absolute inset-0 blur-[40px] opacity-15 pointer-events-none transition-colors duration-1000 ${selectedTier === 'enterprise' ? 'bg-primary' : 'bg-amber-500'}`} />
               <div className="relative p-10 rounded-2xl bg-black/60 backdrop-blur-3xl border border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.6)] flex flex-col gap-10 text-center group-hover:border-white/20 transition-all duration-500">
                  <div className={`w-24 h-24 rounded-2xl flex items-center justify-center self-center shadow-2xl transition-all duration-500 group-hover:rotate-[10deg] ${
                    selectedTier === 'enterprise' ? 'bg-primary text-white shadow-primary/30' : 'bg-amber-500 text-slate-950 shadow-amber-500/20'
                  }`}>
                     <CreditCard size={48} strokeWidth={2.5} />
                  </div>
                  
                  <div className="space-y-4">
                    <div className={`text-[11px] font-black uppercase tracking-[0.5em] animate-pulse ${selectedTier === 'enterprise' ? 'text-primary' : 'text-amber-500'}`}>
                      {selectedTier === 'enterprise' ? t('plan_enterprise') : t('plan_pro')}
                    </div>
                    <div className="text-6xl md:text-7xl font-black text-white flex items-center justify-center gap-2 tracking-tighter">
                       <span className={`text-2xl font-display mt-4 ${selectedTier === 'enterprise' ? 'text-primary/40' : 'text-amber-500/40'}`}>$</span>
                       {monthlyCost.toLocaleString()}
                       <span className="text-[10px] text-slate-600 uppercase font-black self-end mb-4 ml-2 tracking-[0.3em]">ARS</span>
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    <button 
                      onClick={handleSubscribe}
                      disabled={loading || sub.status === 'active'}
                      className={`w-full py-7 rounded-2xl font-black uppercase tracking-[0.4em] text-[11px] flex items-center justify-center gap-4 transition-all relative overflow-hidden shadow-lg hover:scale-[1.03] active:scale-[0.97] ${
                        sub.status === 'active' 
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                        : selectedTier === 'enterprise'
                          ? 'bg-primary text-white hover:bg-blue-600 px-12'
                          : 'bg-amber-500 text-slate-950 hover:bg-amber-400 px-12 shadow-amber-500/10'
                      }`}
                    >
                       {loading ? <Loader2 className="w-7 h-7 animate-spin" /> : (
                         <div className="flex items-center gap-3">
                           {sub.status === 'active' ? <CheckCircle2 size={24} strokeWidth={3} /> : <Zap size={24} fill="currentColor" />}
                           <span>{sub.status === 'active' ? t('active_subscription') : t('upgrade_workspace_cta')}</span>
                         </div>
                       )}
                    </button>
                    <div className="space-y-3">
                      <p className="text-[10px] text-slate-500 font-bold max-w-[280px] mx-auto leading-relaxed flex items-center justify-center gap-2 uppercase tracking-widest">
                         <ShieldCheck size={14} className="text-emerald-500/50" />
                         {t('subscription_disclaimer')}
                      </p>
                    </div>
                  </div>
               </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: COMPARISON */}
      <section className="w-full h-screen snap-center flex flex-col justify-center items-center px-10 relative overflow-hidden">
        <div className="max-w-7xl w-full mx-auto relative z-10">
          <FeatureComparison t={t} />
        </div>
      </section>

      {/* FAQ SECTION */}
        {/* SUCCESS OVERLAY */}
        <AnimatePresence>
          {paymentSuccess && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-black/90 backdrop-blur-xl"
            >
              <motion.div 
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className="max-w-md w-full bg-gradient-to-br from-slate-900 to-black border border-emerald-500/30 p-12 rounded-[2.5rem] text-center shadow-[0_0_100px_rgba(16,185,129,0.2)] relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
                
                <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_40px_rgba(16,185,129,0.4)]">
                   <CheckCircle2 size={48} className="text-white" strokeWidth={3} />
                </div>
                
                <h2 className="text-4xl font-black text-white uppercase tracking-tighter italic mb-4">
                  {t('payment_confirmed')}
                </h2>
                
                <p className="text-slate-400 font-medium text-lg mb-10 leading-relaxed">
                  {t('workspace_updated_success')}
                </p>

                <div className="p-6 bg-white/5 rounded-2xl border border-white/5 mb-10">
                   <div className="flex items-center justify-between mb-2">
                     <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('activeProject')}</span>
                     <span className="text-sm font-black text-white">{activeWorkspace?.name}</span>
                   </div>
                   <div className="flex items-center justify-between">
                     <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Plan</span>
                     <span className="text-sm font-black text-emerald-500 uppercase font-display italic">
                        {paymentSuccess === 'enterprise' ? t('plan_enterprise') : t('plan_pro')}
                     </span>
                   </div>
                </div>

                <button 
                  onClick={() => setPaymentSuccess(null)}
                  className="w-full py-6 rounded-2xl bg-emerald-500 text-slate-900 text-xs font-black uppercase tracking-[0.25em] hover:bg-emerald-400 transition-all shadow-xl"
                >
                   {t('confirmTransferAction')}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default Subscription;
